/**
 * Board color contrast utility.
 *
 * Determines the appropriate text color (white or dark) for content displayed
 * over a board's background color, ensuring WCAG AA compliance (4.5:1 contrast ratio).
 *
 * Uses the relative luminance formula from WCAG 2.1:
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */

import { contrastRatio } from '../theme/contrast-utils';

/** White text color for use on dark backgrounds */
const WHITE_TEXT = '#ffffff';

/** Dark text color for use on light backgrounds — pure black maximizes contrast */
const DARK_TEXT = '#000000';

/**
 * Determines whether text on the given background color should be light or dark
 * to meet WCAG AA contrast requirements (4.5:1 minimum).
 *
 * Returns 'light' if white text provides better contrast (dark background),
 * or 'dark' if dark text provides better contrast (light background).
 *
 * Picks whichever option yields the higher contrast ratio to maximize readability.
 */
export function getContrastMode(backgroundColor: string): 'light' | 'dark' {
  const whiteContrast = contrastRatio(WHITE_TEXT, backgroundColor);
  const darkContrast = contrastRatio(DARK_TEXT, backgroundColor);

  // Pick whichever foreground achieves the higher contrast ratio
  return whiteContrast >= darkContrast ? 'light' : 'dark';
}

/**
 * Returns the appropriate foreground color (hex string) for text displayed
 * on the given board background color. Ensures WCAG AA compliance (4.5:1).
 *
 * Returns white (#ffffff) for dark backgrounds, or black (#000000)
 * for light backgrounds.
 */
export function getBoardTextColor(boardColor: string): string {
  const mode = getContrastMode(boardColor);
  return mode === 'light' ? WHITE_TEXT : DARK_TEXT;
}
